import { Cancel as ErrorIcon, InfoOutlined as InfoIconOutlined } from "@mui/icons-material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { colors } from "Infrastructure/Themes/Colors";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type { ReactNode } from "react";
import { Flex } from "./Core/Flex";

export type AlertVariant = "error" | "success" | "warning" | "info" | "primary" | "gray";

type Props = {
  text?: string;
  title?: string;
  variant?: AlertVariant;
  content?: ReactNode;
  action?: ReactNode;
  bordered?: boolean;
};

const variantConfig: Record<AlertVariant, { color: string; bgColor: string; borderColor: string; icon: ReactNode }> = {
  error: {
    color: "error.dark",
    bgColor: "error.light",
    borderColor: "error.dark",
    icon: <ErrorIcon sx={{ color: "error.dark" }} />,
  },
  success: {
    color: "success.dark",
    bgColor: "success.light",
    borderColor: "success.dark",
    icon: <CheckCircleOutlinedIcon sx={{ color: "success.dark" }} />,
  },
  warning: {
    color: "warning.dark",
    bgColor: "warning.light",
    borderColor: "warning.dark",
    icon: <WarningAmberOutlinedIcon sx={{ color: "warning.dark" }} />,
  },
  info: {
    color: "info",
    bgColor: colors().InfoSuperLight,
    borderColor: "info.dark",
    icon: <InfoIconOutlined sx={{ color: "info.main" }} />,
  },
  primary: {
    color: "primary",
    bgColor: "primary.light",
    borderColor: "primary.dark",
    icon: <InfoOutlinedIcon sx={{ color: "primary.main" }} />,
  },
  gray: {
    color: "grey.500",
    bgColor: "grey.50",
    borderColor: "grey.500",
    icon: <InfoOutlinedIcon sx={{ color: "grey.700" }} />,
  },
};

export const Alert = ({ title, text, content, variant = "error", action, bordered }: Props) => {
  const config = variantConfig[variant];

  return (
    <Flex
      alignItems={"center"}
      borderRadius={Radius.M}
      py={Spacing.L}
      px={Spacing.XL}
      border={bordered ? "1px solid" : undefined}
      borderColor={bordered ? config.borderColor : undefined}
      sx={{ backgroundColor: config.bgColor, boxSizing: "border-box" }}
    >
      {config.icon}
      <Stack ml={Spacing.L} spacing={Spacing.S} flex={1}>
        {title && (
          <Typography lineHeight={1} variant="body2" color={config.color} fontWeight={600}>
            {title}
          </Typography>
        )}

        {text && (
          <Typography lineHeight={1.3} variant="body2" color={config.color}>
            {text}
          </Typography>
        )}

        {content && (
          <Box ml={title ? Spacing.S : Spacing.L} sx={{ color: config.color }}>
            {content}
          </Box>
        )}
      </Stack>

      {action && <Box ml={Spacing.L}>{action}</Box>}
    </Flex>
  );
};
